import React from 'react';
import type { DialogOption, TrainingOption } from '../../../../shared/NPCData';
import { TomeButton, TomeChapter, TomeEntry } from '../../primitives/tome';

export interface TrainingViewProps {
  options: readonly TrainingOption[];
  playerLevel: number;
  playerGold?: number;
  onDialogSelect?: (option: DialogOption) => void;
  pendingAbilityId?: string | null;
  refusal?: { abilityId: string; message: string } | null;
}

/** Quotes and committed training state come from the server; this view predicts neither. */
export const TrainingView: React.FC<TrainingViewProps> = ({
  options, playerLevel, playerGold, onDialogSelect, pendingAbilityId, refusal,
}) => (
  <TomeChapter title="Learn a new ability" className="npc-training" aria-label="Ability training">
    <p className="npc-section-lede">Choose a technique to add to your spellbook.</p>
    {options.length === 0 && <p role="status" className="npc-empty">No abilities are available to learn here at your current level.</p>}
    {options.map(option => {
      const validQuote = option.quote.action === 'ability_training' && Number.isSafeInteger(option.quote.cost) && option.quote.cost >= 0;
      const levelReady = playerLevel >= option.requiredLevel;
      const affordable = validQuote && (option.quote.cost === 0 || (playerGold !== undefined && playerGold >= option.quote.cost));
      const pending = pendingAbilityId === option.abilityId;
      const disabled = !onDialogSelect || !validQuote || !levelReady || !affordable || Boolean(pendingAbilityId);
      const cost = validQuote ? (option.quote.cost === 0 ? 'Free' : `${option.quote.cost.toLocaleString()} gold`) : 'Cost unavailable';
      const action = pending ? 'Learning…' : !levelReady ? `Requires level ${option.requiredLevel}`
        : !affordable && validQuote ? (playerGold === undefined ? 'Checking gold…' : 'Not enough gold') : 'Learn ability';
      return <TomeEntry key={option.abilityId} className="npc-lesson" aria-label={option.name} tone={disabled ? 'default' : 'available'}
        kicker={`Available from level ${option.requiredLevel}`} title={option.name}>
        <div className="npc-lesson-body">
          <span className="npc-lesson-icon" aria-hidden="true">
            {option.icon.startsWith('/') ? <img src={option.icon} alt="" width={42} height={42} /> : option.icon}
          </span>
          <div>
            <p>{option.description}</p>
            {option.stats && option.stats.length > 0 && (
              <ul className="npc-lesson-stats" aria-label={`${option.name} numbers`}>
                {option.stats.map(stat => <li key={stat}>{stat}</li>)}
              </ul>
            )}
          </div>
        </div>
        <div className="npc-card-action-row">
          <span className="npc-quoted-price">{cost}</span>
          <TomeButton tone="primary" disabled={disabled} aria-label={`${action}: ${option.name}`}
            onClick={() => onDialogSelect?.({ text: `Learn ${option.name}`, icon: option.icon, action: `learnAbility:${option.abilityId}` })}>
            {action}
          </TomeButton>
        </div>
        {refusal?.abilityId === option.abilityId && <p role="alert" className="npc-purchase-refusal">{refusal.message}</p>}
      </TomeEntry>;
    })}
  </TomeChapter>
);
